import React from 'react'

import classnames from 'classnames'

import { checkLocale } from '@toasttab/buffet-pui-locale-utilities'

import { useThrottledTracker } from 'src/lib/js/hooks/useTracker'
import { useFormatPriceCallback } from 'src/lib/js/hooks/useFormatPrice'
import { t, Trans } from 'src/translations'
import { AuthenticationSource } from 'public/components/default_template/online_ordering/checkout/checkoutUtils'
import { PwlessAuth } from 'public/components/default_template/online_ordering/account/pwlessAuth/PwlessAuth'

import {
  Modal,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  useModal
} from 'shared/components/common/modal'
import { useRestaurant } from 'shared/components/common/restaurant_context/RestaurantContext'
import { TrackingEvents } from 'shared/components/common/tracking/TrackingEvents'

import { useCustomer } from 'public/components/online_ordering/CustomerContextCommon'

import {
  FreeDeliveryIllustration,
  FreeDeliveryIllustrationPalette
} from './OffersIcons'
import type { FreeDeliveryOfferData } from './useFreeDeliveryOffer'
import { useBostonFreeDeliveryTerms } from './useFreeDeliveryOffer'
import { useStyledCardColors } from './useStyledCardColors'

const CARD_ID = 'free-delivery-offer-card'

const TERMS_URL =
  'https://guesthelp.toasttab.com/en/articles/16630834-toast-local-free-delivery-boston-promotional-offer-terms'

// A fixed Toast palette rather than the merchant's theme colors: this is a Toast
// promotion, so the bag looks the same on every storefront. The shaded face is
// Toast red rather than a darkened orange, so it cannot be derived from the base
// color the way the themed offer cards derive theirs.
const BAG_COLOR = '#FF9400'
const BAG_PALETTE: FreeDeliveryIllustrationPalette = {
  flap: '#F28900',
  label: '#333333',
  panel: '#FF4C00'
}

type ToastFundedFreeDeliveryOfferProps = {
  offer: FreeDeliveryOfferData
}

export const ToastFundedFreeDeliveryOffer = ({
  offer
}: ToastFundedFreeDeliveryOfferProps) => {
  const {
    isOpen: isAuthModalOpen,
    onClose: onAuthModalClose,
    onOpen: onAuthModalOpen
  } = useModal()
  const {
    isOpen: isDetailsModalOpen,
    onClose: onDetailsModalClose,
    onOpen: onDetailsModalOpen
  } = useModal()
  const { borderColor, templateOverrides } = useStyledCardColors()
  const { track } = useThrottledTracker()
  const { ooRestaurant } = useRestaurant()

  const { isLoggedIn, loadingCustomer } = useCustomer()
  const autoApplies = isLoggedIn || loadingCustomer

  const onCardClick = () => {
    if (autoApplies) {
      onDetailsModalOpen()
      return
    }

    track(TrackingEvents.STARTED_AUTHENTICATION, {
      source: AuthenticationSource.FreeDeliveryOfferCard,
      offerGuid: offer.guid,
      offerName: offer.offerName
    })
    onAuthModalOpen()
  }

  // The central auth events miss new signups.
  const onSuccessfulLogin = () => {
    track(TrackingEvents.FREE_DELIVERY_OFFER_LOGIN_COMPLETED, {
      source: AuthenticationSource.FreeDeliveryOfferCard,
      offerGuid: offer.guid,
      offerName: offer.offerName
    })
    onAuthModalClose()
  }
  // trailingZeros false: the threshold is a whole dollar amount, so "$15" rather
  // than "$15.00".
  const formatPrice = useFormatPriceCallback()
  const title = t('offers.free-delivery-minimum-title', {
    amount: formatPrice(Number(offer.minPurchaseAmount.amount), false)
  })

  const terms = useBostonFreeDeliveryTerms(offer, !autoApplies)

  const termsCopy = terms ? (
    <Trans
      i18nKey={
        terms.endDate
          ? 'offers.free-delivery-terms'
          : 'offers.free-delivery-terms-no-end-date'
      }
      values={{
        maxAmount: formatPrice(Number(terms.maxDeliveryValue.amount), false),
        minAmount: formatPrice(Number(terms.minPurchaseAmount.amount), false),
        endDate: terms.endDate
          ? new Intl.DateTimeFormat(checkLocale(ooRestaurant?.i18n?.locale), {
              dateStyle: 'long',
              timeZone: ooRestaurant?.timeZoneId ?? undefined
            }).format(new Date(terms.endDate))
          : ''
      }}
      components={{
        TermsLink: (
          <a href={TERMS_URL} target='_blank' rel='noopener noreferrer'>
            {/* content */}
          </a>
        )
      }}
    />
  ) : null

  const cardClassName = classnames(
    'styledCard',
    'freeDeliveryOfferCard',
    templateOverrides.borderStroke,
    templateOverrides?.roundedCorner,
    templateOverrides?.dropShadow
  )

  const cardStyle = { borderColor }

  const cardContent = (
    <div className='cardContent'>
      <div className='cardInfo'>
        <div className='cardTitleGroup'>
          <div className='cardText' data-testid='offer-text'>
            {title}
          </div>
        </div>
        <div className='cardDetails'>
          {autoApplies
            ? t('offers.automatically-applied')
            : t('offers.free-delivery-log-in-to-apply')}
        </div>
      </div>
      <div
        className='illustration freeDeliveryIllustration'
        data-testid='offer-illustration-freeDelivery'
      >
        <FreeDeliveryIllustration
          color={BAG_COLOR}
          palette={BAG_PALETTE}
          altText={t('offers.free-delivery-bag')}
        />
      </div>
    </div>
  )

  return (
    <>
      <button
        id={CARD_ID}
        className={cardClassName}
        style={cardStyle}
        onClick={onCardClick}
        data-testid={`offer-${offer.guid}`}
      >
        {cardContent}
      </button>
      {/* Repeats Offers.tsx's OfferDetail markup, which is private to a module
          that imports this card. */}
      <Modal
        isOpen={isDetailsModalOpen}
        onClose={onDetailsModalClose}
        className='offerModalWrapper'
      >
        <ModalOverlay fadeIn fadeOut />
        <ModalContent
          contentClassName='offerModal'
          returnFocusElementId={CARD_ID}
        >
          <ModalCloseButton />
          <div className='content' data-testid='free-delivery-offer-modal'>
            <div
              className='header'
              style={{
                background: `linear-gradient(0deg, white, ${BAG_COLOR}22)`
              }}
            >
              <FreeDeliveryIllustration
                color={BAG_COLOR}
                palette={BAG_PALETTE}
                altText={t('offers.free-delivery-bag')}
              />
              <div className='title'>{title}</div>
              <div>{t('offers.automatically-applied')}</div>
            </div>
            {termsCopy && (
              <div className='body'>
                <div className='offerDetail'>
                  <div className='detail'>
                    <div className='detailTitle'>
                      {t('offers.restrictions')}
                    </div>
                    <div className='detailDescription'>{termsCopy}</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </ModalContent>
      </Modal>
      <Modal
        isOpen={isAuthModalOpen}
        onClose={onAuthModalClose}
        className='authModalWrapper'
      >
        <ModalOverlay fadeIn fadeOut />
        <PwlessAuth
          source={AuthenticationSource.FreeDeliveryOfferCard}
          onSuccessfulLogin={onSuccessfulLogin}
          returnFocusElementId={CARD_ID}
        />
      </Modal>
    </>
  )
}
